<script lang="ts" setup>
import { useFieldWithErrors } from '@consumer/composables/fieldWithErrors'
import type { Placement } from 'floating-vue'
import { uniqueId } from '@consumer/helpers/string'
import newCheckIcon from '@consumer/assets/icons/new/check.svg?svgicon'

const {
  modelValue: checked,
  label,
  large,
  id,
  name,
  disabled = false,
  error,
  align = 'center',
  errorTooltip,
} = defineProps<{
  label?: string
  modelValue?: boolean
  large?: boolean
  id?: string
  name: string
  disabled?: boolean
  error?: any
  errorTooltip?: boolean | Boolean | Placement
  align?: 'start' | 'center'

  // Provided automatically by GInput.
  hasErrors?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: any]
}>()

const slots = useSlots()

const hasLabel = computed(() => label || slots.default)

const inputId = computed(() => id ?? uniqueId('g-checkbox-'))
const inputElement = ref<HTMLInputElement>()

function onInput (event: Event) {
  const target = event?.target as HTMLInputElement
  emit('update:modelValue', target.checked)
}

const {
  activate,
  activated,
  showError,
} = useFieldWithErrors(inputElement, () => error, () => errorTooltip)
activate()
defineExpose({ inputElement, activate, activated })

const size = $computed(() => large ? '24px' : '18px')
</script>

<template>
  <label :class="`relative flex items-${align} cursor-pointer ml-1`">
    <div
      class="rounded-v2xs flex flex-shrink-0 justify-center items-center
        focus-within:border-primary-dark box-border cursor-pointer p-1"
      :class="[
        align === 'start' ? 'mt-0.5': '',
        checked ? 'bg-primary-base border-0' : 'bg-white border-1 border-grey-500 border-opacity-70',
        hasErrors || showError ? '!border-red-base' : ''
      ]"
      :style="{ height: size, width: size }"
    >
      <GIcon v-if="checked" :svg="newCheckIcon" color="white" class="stroke-2"/>
    </div>
    <span v-if="hasLabel" :for="inputId" class="ml-2 cursor-pointer select-none">
      <slot>
        {{ label }}
      </slot>
    </span>
    <input
      :id="inputId"
      ref="inputElement"
      :checked="checked"
      :name="name"
      type="checkbox"
      class="opacity-0 absolute"
      :disabled="disabled"
      :style="{ height: size, width: size }"
      @input.prevent="onInput"
    >
  </label>
</template>
